import {
  AfterViewInit,
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  ElementRef, EventEmitter, HostListener,
  Inject,
  Input,
  OnInit, Optional, Output,
  TemplateRef,
  ViewChild
} from "@angular/core";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {DROPDOWN_ANIMATION} from "../../../animations";
import {BehaviorSubject, combineLatest, EMPTY, merge, Observable} from "rxjs";
import {DOCUMENT} from "@angular/common";
import {switchMap, take, takeUntil} from "rxjs/operators";

import {DestroyStreamService, SatelliteService} from "../../../services";
import {IntInputComponent} from "../../int-input/int-input.component";
import {IFSelectDataPayload, IntTestingDataMarkers, TVerticalAlign} from "../../../types";
import {KEYBOARD_ACTIONS} from "../../../enums";
import {INT_EMPTY_LIST_LABEL, INT_TESTING_DATA_MARKERS_TOKEN} from "../../../tokens";



@Component({
  selector: 'int-filter-select',
  templateUrl: './int-filter-select.component.html',
  styleUrls: ['./int-filter-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    SatelliteService,
    DestroyStreamService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: IntFilterSelectComponent,
      multi: true,
    }
  ],
  animations: [DROPDOWN_ANIMATION],
})
export class IntFilterSelectComponent implements OnInit, AfterViewInit, ControlValueAccessor {
  focus$ = new BehaviorSubject(false);
  open$ = new BehaviorSubject(false);
  value$ = new BehaviorSubject<string | null>(null);
  label$ = new BehaviorSubject<string | null>(null);
  list$ = new BehaviorSubject<Array<IFSelectDataPayload>>([]);
  inputElement$ = new BehaviorSubject<HTMLElement | null>(null);

  disabled = false;
  touched = false;

  selectIndex$ = new BehaviorSubject<number | null>(null);

  @Input()
  set value(value: string | null) {
    this.value$.next(value);
  }

  @Input()
  setListAfterFocus: boolean = false;

  @Input() set list(data: Array<IFSelectDataPayload>) {
    if(!this.isListDouble(this.list$.value, data)) {
      this.list$.next(data)
    }
  };

  predicateList$ = new BehaviorSubject<Array<IFSelectDataPayload>>([]);
  selectValue$ = new BehaviorSubject<IFSelectDataPayload | null>(null);

  @Input() filterPredicate!: (value: string, cb: (data: Array<IFSelectDataPayload>) => void) => void;

  @Input()
  dropContent!: TemplateRef<any>;

  @Input()
  previewContent!: TemplateRef<any>;

  @Input()
  emptyContent!: TemplateRef<any>;

  @Input()
  verticalAlign: TVerticalAlign = 'center';

  @Input()
  dropdownHeight = '7rem';

  @Input() placeholder = '';
  @Input() label = '';

  @Input() useAutoscrollToSelectedValue = false;

  @Input() listItemTestingMark: string | null = null;

  @ViewChild('select', { static: true }) private readonly select!: ElementRef<any>;
  @ViewChild('input', {read: ElementRef}) readonly input!: ElementRef<any>;
  @ViewChild('input') readonly inputComponentRef!: IntInputComponent;

  @Output() focusEvent = new EventEmitter();

  @Output()
  changeValueEvent: EventEmitter<string> = new EventEmitter<string>();

  _onSelect = this.onSelect.bind(this);

  lastBlur$ = new BehaviorSubject<null | Date>(null);

  constructor(
    @Inject(DOCUMENT) private readonly documentRef: Document,
    private readonly destroy$: DestroyStreamService,
    private readonly cdr: ChangeDetectorRef,
    @Inject(INT_EMPTY_LIST_LABEL) readonly emptyLabel$: Observable<string>,
    @Optional()
    @Inject(INT_TESTING_DATA_MARKERS_TOKEN) readonly testingMarkers: IntTestingDataMarkers
  ) {
    this.open$.pipe(
      switchMap((status) => {
        return (status && this.useAutoscrollToSelectedValue) ? combineLatest([this.predicateList$, this.value$])
          .pipe(take(1)) : EMPTY;
      }))
      .pipe(takeUntil(this.destroy$))
      .subscribe(([list, value]) => {
        this.setSelectedID(list, value);
      });

    this.open$.pipe(takeUntil(this.destroy$)).subscribe((status) => {
      if(!status && !this.useAutoscrollToSelectedValue) {
        this.selectIndex$.next(null);
      }
    })
  }


  @HostListener('window:keydown', ['$event'])
  keypress(e: KeyboardEvent) {

    if(!this.isDropped) {
      return;
    }

    const keyCode = e.keyCode;
    if(keyCode === KEYBOARD_ACTIONS.PREVIOUS) {
      this.navigate(-1);
    }
    if(keyCode === KEYBOARD_ACTIONS.NEXT) {
      this.navigate(1);
    }
    if(keyCode === KEYBOARD_ACTIONS.ENTER) {
      this.onKeyPressedSelect();
    }
  }

  getRecordByValue(_value: string): IFSelectDataPayload | undefined {
    return this.list$.value.find(({value}) => {
      return value === _value;
    })
  }

  isListDouble(prev: Array<IFSelectDataPayload>, current: Array<IFSelectDataPayload>) {
    return current.every((item, index) => {
      return prev[index]
        ? (prev[index].label === item.label) && (prev[index].value === item.value)
        : false;
    })
  }

  ngAfterViewInit() {
    this.inputElement$.next(this.input.nativeElement);
  }

  ngOnInit() {
    if (!this.setListAfterFocus) {
      this.setPredicateListByValue();
    }

    this.list$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.setListAfterFocus) {
          this.setPredicateListByValue();
        }
      });

    merge(this.predicateList$, this.value$, this.list$)
      .subscribe((v) => {
        const selected = (this.predicateList$.value || []).find(
          (i) => i.value === this.value$.value
        );
        this.selectValue$.next(selected || null);
        if (selected) {
          this.label$.next(selected.label);
        }
      });
  }

  get currentValue() {
    return this.value$.value;
  }

  get isPreviewShow() {
    return (this.isValidValue(this.currentValue) ? `${this.currentValue}` : '').length > 0;
  }

  get isDropped(): boolean {
    return this.open$.value;
  }

  get isFocused(): boolean {
    return this.focus$.value;
  }

  get listLength() {
    return (this.predicateList$.value || []).length;
  }

  setSelectedID(list: Array<IFSelectDataPayload>, value: string | null) {
    const _list = (list || []).map(({value}) => value);
    const currentValue = value || '';
    const id = (_list && _list.length) && currentValue ? _list.indexOf(currentValue) : null;
    this.selectIndex$.next(id);
  }

  navigate(direction: number) {
    const _i = (this.selectIndex$.value || 0) + direction;
    const currentActiveId =
      this.selectIndex$.value === null
      ? 0
      : _i < 0
      ? this.listLength - 1
      : _i > this.listLength - 1
        ? 0
        : _i;

    this.selectIndex$.next(currentActiveId)
  }

  closeDropper() {
    this.open$.next(false);
    this.cdr.markForCheck();
  }

  openDropper() {
    this.open$.next(true);
    this.cdr.markForCheck();
  }

  isValidValue(value: unknown) {
    return (typeof value === 'number') || (typeof value === 'string');
  }

  toggleInputFocusHandler(e: Event) {
    const date = new Date().getTime();
    const blurTime = this.lastBlur$.value?.getTime();
    //
    // Hack fix it!
    //
    if((blurTime !== null) && (blurTime !== undefined) && (date - blurTime >= 0)) {
      if(date - blurTime >= 400) {
        this.inputComponentRef?.setFocus();
      }
      this.lastBlur$.next(null);
      return;
    }else{
      setTimeout(() => {
        if(this.focus$.value) {
          this.inputComponentRef?.setBlur();
        }else{
          this.inputComponentRef?.setFocus();
        }
        this.lastBlur$.next(null);
      }, 50);
    }
    //
    //
    //
  }

  onChange(value: string) {}

  onTouched() {}

  writeValue(value: string) {
    this.selectValue$.next(null)
    this.value$.next(value);

    if(this.inputComponentRef) {

      const record = this.getRecordByValue(value);

      const _value = record ? record.label : value;

      this.inputComponentRef.value = _value;
      this.setPredicateListByValue();
    }
    this.cdr.detectChanges();
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  defaultPredicate(
    value: string,
    list: Array<IFSelectDataPayload>
  ): Array<IFSelectDataPayload> {
    return list.filter((item: IFSelectDataPayload) => {
      return (item?.label?.toLowerCase() || '').includes(value.toLowerCase());
    });
  }

  setPredicateListByValue(value: string = '') {
    if (this.filterPredicate) {
      this.filterPredicate(value, (data) => {
        this.predicateList$.next(data);
      });
    } else {
      this.predicateList$.next(this.defaultPredicate(value, this.list$.value));
    }
  }

  onSelect(payload: IFSelectDataPayload) {
    this.value$.next(payload.value);
    this.label$.next(payload.label);
    this.selectValue$.next(payload);
    this.changeValueEvent.emit(payload.value);
    if(this.inputComponentRef && Boolean(payload.label)) {
      this.inputComponentRef.value = payload.label;
    }
    this.onChange(payload.value);
    this.closeDropper();
  }

  onKeyPressedSelect() {
    const payload = (this.predicateList$.value || []).find(({value}, index) => {
      return index === this.selectIndex$.value//value === this.value$.value;
    });
    if(payload) {
      this.onSelect(payload);
      this.inputComponentRef.setBlur();
    }
  }

  onFocus(status: boolean) {
    if(status) {

      if (this.setListAfterFocus) {
        this.setPredicateListByValue();
      }

      this.focus$.next(true);

      this.openDropper();

      this.focusEvent.emit();
    }
    this.cdr.detectChanges();
  }

  onBlur(status: boolean) {
    if(status) {
      this.lastBlur$.next(new Date());
      this.focus$.next(false);

      this.inputComponentRef.value = this.label$.value || '';

      this.setPredicateListByValue();
      this.closeDropper();
      this.lastBlur$.next(new Date())
    }
  }

  onInput(value: string) {
    this.selectValue$.next(null);
    this.setPredicateListByValue(value);
  }

}
